import { FC, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Container, Grid, Toolbar } from '@mui/material';
import { Session } from '@ory/client';

import { AppBar } from '../components/appBar/appBar';
import { SideBar } from '../components/sideBar/sideBar';
import AppSpeedDial from '../components/appSpeedDial/appSpeedDial';

import { authActions } from '../store/auth/auth';
import { authoritiesListSelector, authStatusSelector } from '../store/selectors';
import { createOry, oryConfig } from '../services/ory';
import { useLazyGetSessionQuery } from '../api';
import { useAppActions, useAppSelector } from '../utils/hooks';
import { SocketProvider } from '../services';
import { useLazyGetMyAuthoritiesQuery } from '../api/controllers/admin-roles';

export const oryLoginUrl = `${process.env.REACT_APP_UI_ORY_SDK_URL}/login?return_to=${window.location.href}`;
export const redirectToOryLogin = () => window.location.replace(oryLoginUrl);

const ory = createOry(oryConfig);

export const DashboardLayout: FC = () => {
  const location = useLocation();
  const { setAuthStatus, setLogoutUrl } = useAppActions(authActions);
  const authStatus = useAppSelector(authStatusSelector);
  const authoritiesList = useAppSelector(authoritiesListSelector);
  const [getAuthorities] = useLazyGetMyAuthoritiesQuery();
  const [getWhoami] = useLazyGetSessionQuery();
  const [session, setSession] = useState<Session | undefined>();

  useEffect(() => {
    if (!authoritiesList?.length) {
      getAuthorities();
    }
  }, []);

  useEffect(() => {
    getWhoami().then((session) => {
      // @ts-ignore
      if (session.error && session.error?.status) {
        setAuthStatus(false);
      } else {
        setAuthStatus(true);
        // @ts-ignore
        setSession(session);
      }
      ory.createBrowserLogoutFlow().then(({ data }) => {
        setLogoutUrl(data.logout_url);

        // @ts-ignore
        if (session.error?.status) {
          window.location.href = String(data.logout_url);
        }
      });
    });
  }, []);

  const content = useMemo(() => {
    if (!session || !authoritiesList?.length) {
      return (
        <Box sx={{ display: 'flex', height: '100vh', width: '100vh' }}>
          <CircularProgress
            size={75}
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        </Box>
      );
    }

    return (
      <SocketProvider>
        <Box sx={{ display: 'flex' }}>
          <AppSpeedDial />
          <AppBar />
          <SideBar />

          <Box
            component="main"
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
              flexGrow: 1,
              height: '100vh',
              overflow: 'auto',
            }}
          >
            <Toolbar />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
              <Grid container spacing={3}>
                <Outlet />
              </Grid>
            </Container>
          </Box>
        </Box>
      </SocketProvider>
    );
  }, [session, authStatus, authoritiesList]);

  return content;
};
